import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Truncate } from '@ui/Truncate/Truncate'
import { Body2 } from '@ui/Typography'
import { Modal } from '@ui/components/Modal'
import { TableHeaderTooltip } from '@ui/v2/Table/TableHeaderTooltip'

import { Box } from '@shared/components/Box'

import { VIEW_MORE_ARROW_COL_KEY } from '../hooks/useTableSearch'

import styles from './TableViewMore.modal.module.scss'

type NormalizedViewMoreData = { id: string; name: string; value: string | React.ReactNode }[]
export interface TableViewMoreModalProps<T = any> {
  data: T[]
  initialIndex: number
  formatCtx?: any
  columns: {
    id?: string | null
    labelKey: string
    getValue: (data: any, formatCtx?: any) => string | React.ReactNode
  }[]
}

type TitleTooltipHeaderId = 'FxmVolume' | 'OtherVolume' | 'FxmAmount' | 'OtherAmount'

export function TableViewMoreModal<T>({
  data,
  initialIndex,
  formatCtx,
  columns,
}: TableViewMoreModalProps<T>) {
  const { t } = useTranslation()

  const titleTooltipHeaderIds: Record<TitleTooltipHeaderId, string> = {
    FxmVolume: t('IB.RebatesPage.Forex & Metals'),
    OtherVolume: t('IB.RebatesPage.Other instrument groups'),
    FxmAmount: t('IB.RebatesPage.Forex & Metals'),
    OtherAmount: t('IB.RebatesPage.Other instrument groups'),
  }

  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const currentVisibleData = data[currentIndex]
  const normalizedData: NormalizedViewMoreData = columns
    .filter(
      (col): col is typeof col & { id: string } =>
        col.id !== undefined && col.id !== VIEW_MORE_ARROW_COL_KEY
    )
    .map((col) => ({
      id: col.id,
      name: t(col.labelKey),
      value: col.getValue(currentVisibleData, formatCtx),
    }))

  const dataRows = normalizedData.map(({ name, value, id }) => {
    return (
      <Box flex justify='space-between' gap='x8' fullWidth key={name}>
        <div className={styles.nameContainer}>
          <Truncate>
            <Body2 color='text-low-em'>{name}</Body2>
          </Truncate>

          <TableHeaderTooltip
            headerIds={titleTooltipHeaderIds}
            targetHeaderId={id as TitleTooltipHeaderId}
          />
        </div>

        <div className={styles.valueContainer}>
          {typeof value === 'string' ? (
            <Truncate>
              <Body2 color='text-high-em'>{value}</Body2>
            </Truncate>
          ) : (
            value
          )}
        </div>
      </Box>
    )
  })

  const headerTitle = normalizedData.length > 0 ? normalizedData[0].value : ''

  return (
    <Modal.FullScreen>
      <Modal.Header canClose>{headerTitle}</Modal.Header>
      <Modal.Body>{dataRows}</Modal.Body>
      <Modal.Footer direction='row'>
        <Modal.Footer.Secondary
          onClick={() => setCurrentIndex((i) => i - 1)}
          disabled={currentIndex === 0}
        >
          {t('tour.Previous')}
        </Modal.Footer.Secondary>
        <Modal.Footer.Secondary
          onClick={() => setCurrentIndex((i) => i + 1)}
          disabled={currentIndex === data.length - 1}
        >
          {t('tour.Next')}
        </Modal.Footer.Secondary>
      </Modal.Footer>
    </Modal.FullScreen>
  )
}
